import { router } from "@inertiajs/react";
import { Users } from "lucide-react";
import { useCallback } from "react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getIcon } from "@/lib/icons";
import { bizminer_app_pillar_insight_scoring_category_path } from "@/routes";
import type {
  BizminerPeerData,
  BizminerPillarInsight,
} from "@/types/serializers";
import { PeerSelection } from "./_components/PeerSelection";
import { RadarChart } from "./_components/RadarChart";
import { ScoringDataTable } from "./_components/ScoringDataTable";
import { ScoringSummary } from "./_components/ScoringSummary";
import { useScoringPeers } from "./_components/useScoringPeers";

// ─── Props ───────────────────────────────────────────────
interface Props {
  pillar_insight: BizminerPillarInsight;
  peer_data: BizminerPeerData;
}

// ═══════════════════════════════════════════════════════════
// Scoring Overview
// ═══════════════════════════════════════════════════════════
export default function Scoring({ pillar_insight, peer_data }: Props) {
  const IconComponent = getIcon(pillar_insight.icon) || Users;
  const categories = pillar_insight.analysis.categories;

  const {
    peer_pool,
    selected_peer_ids,
    peer_columns,
    categories_with_peers,
    peer_names,
    addPeer,
    removePeer,
  } = useScoringPeers({
    peerData: peer_data,
    pillarInsightId: pillar_insight.id,
  });

  const handleAxisClick = useCallback(
    (categoryName: string) => {
      const cat = categories.find((c) => c.name === categoryName);
      if (cat) {
        router.visit(
          bizminer_app_pillar_insight_scoring_category_path(
            pillar_insight.id,
            cat.id,
          ),
        );
      }
    },
    [categories, pillar_insight.id],
  );

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="flex size-12 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
            <IconComponent className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Scoring & Assessment
            </h1>
            <p className="text-muted-foreground">{pillar_insight.title}</p>
          </div>
        </div>

        <PeerSelection
          selectedPeerIds={selected_peer_ids}
          peerPool={peer_pool}
          peerColumns={peer_columns}
          onAdd={addPeer}
          onRemove={removePeer}
        />

        <ScoringSummary analysis={pillar_insight.analysis} />

        <Card>
          <CardHeader className="pb-0">
            <CardTitle>AI Maturity Assessment</CardTitle>
            <CardDescription>
              Performance across key metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <RadarChart
              accountName={pillar_insight.account_name}
              competitorNames={peer_names}
              categories={categories_with_peers}
              height={650}
              outerRadius="75%"
              onAxisClick={handleAxisClick}
            />
          </CardContent>
        </Card>

        <ScoringDataTable
          competitorNames={peer_names}
          categories={categories_with_peers}
          account_name={pillar_insight.account_name}
          pillarTitle={pillar_insight.title}
          pillarInsightId={pillar_insight.id}
        />
      </div>
    </BizminerLayout>
  );
}
